import axios, { AxiosResponse } from "axios";
import http from "../../http-common";

import {
    WorkdayCertification,
    WorkdayCertificationListResponseClientError,
    WorkdayCertificationListResponseOk,
    WorkdayCertificationListUnifiedResponse,
} from "../../types/Workday/WorkdayCertification";
import {
    WorkdayTopic,
    WorkdayTopicListResponseClientError,
    WorkdayTopicListResponseOk,
    WorkdayTopicListUnifiedResponse,
} from "../../types/Workday/WorkdayTopic";
import {
    WorkdayEmployeeCertification,
    WorkdayEmployeeCertificationListUnifiedResponse,
} from "../../types/Workday/WorkdayEmployeeCertification";

export const getWorkdayCertifications = async (): Promise<WorkdayCertificationListUnifiedResponse> => {
    const certifications: WorkdayCertification[] = [];
    let page: number | null = 1;
    let lastResponse: AxiosResponse<
        WorkdayCertificationListResponseOk | WorkdayCertificationListResponseClientError | string
    > | undefined;

    try {
        while (page !== null) {
            const response: AxiosResponse<
                WorkdayCertificationListResponseOk | WorkdayCertificationListResponseClientError | string
            > = await http.get(`/listWorkdayCertifications?Page=${page}`);
            lastResponse = response;

            if (response.status >= 200 && response.status < 300) {
                const data = response.data;

                if (typeof data === 'object' && data !== null && 'items' in data) {
                    const okData = data as WorkdayCertificationListResponseOk;
                    certifications.push(...(okData.items ?? []));
                    page = okData.nextPage ?? null;
                    continue;
                }

                return {
                    ok: true,
                    statusCode: response.status,
                    message:
                        typeof data === 'string'
                            ? data
                            : 'Workday certifications retrieved (unknown format)',
                    certifications: null,
                };
            }

            const errorData = response.data as WorkdayCertificationListResponseClientError;
            return {
                ok: false,
                statusCode: errorData.status ?? response.status,
                message:
                    errorData.title ??
                    response.statusText ??
                    'Error retrieving Workday certifications',
                certifications: null,
                error: errorData,
            };
        }

        return {
            ok: true,
            statusCode: lastResponse?.status ?? 200,
            message: 'Workday certifications retrieved successfully',
            certifications,
        };
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const statusCode = error.response?.status ?? 500;
            const message =
                error.response?.data?.message ||
                error.message ||
                'An error occurred while retrieving Workday certifications.';

            return {
                ok: false,
                statusCode,
                message,
                certifications: null,
                error: error.response?.data ?? error.message,
            };
        }

        return {
            ok: false,
            statusCode: 500,
            message:
                (error as Error)?.message ??
                'Unknown error while retrieving Workday certifications.',
            certifications: null,
            error: `${error}`,
        };
    }
};


/** Optional server-side filters for the topic list. Both default to false. */
export type WorkdayTopicQueryOptions = {
    standardWorkTrainerOnly?: boolean; // only topics actively linked to a Standard Work Trainer cert
    eligibleCoursesOnly?: boolean;     // drop topics left with no eligible courses
};

/**
 * Retrieves Workday topics, following the paging links until every page is collected.
 *
 * Courses returned under each topic are always limited to eligible course IDs by the API. The two
 * options additionally restrict which topics come back, and are opt-in because this endpoint also
 * feeds the topic picker on the Standard Work Trainer Certificates page — that caller needs the
 * unfiltered list or a topic that is not yet linked could never be found and linked.
 *
 * @param options - Server-side topic filters; omit for the unfiltered list.
 * @returns A unified response carrying the collected topics, or an error message on failure.
 */
export const getWorkdayTopics = async (
    options: WorkdayTopicQueryOptions = {},
): Promise<WorkdayTopicListUnifiedResponse> => {
    const topics: WorkdayTopic[] = [];
    let page: number | null = 1;
    let lastResponse: AxiosResponse<
        WorkdayTopicListResponseOk | WorkdayTopicListResponseClientError | string
    > | undefined;

    const filterParams = [
        options.standardWorkTrainerOnly ? '&StandardWorkTrainerOnly=true' : '',
        options.eligibleCoursesOnly ? '&EligibleCoursesOnly=true' : '',
    ].join('');

    try {
        while (page !== null) {
            const response: AxiosResponse<
                WorkdayTopicListResponseOk | WorkdayTopicListResponseClientError | string
            > = await http.get(`/listWorkdayTopics?Page=${page}${filterParams}`);
            lastResponse = response;

            if (response.status >= 200 && response.status < 300) {
                const data = response.data;

                if (typeof data === 'object' && data !== null && 'items' in data) {
                    const okData = data as WorkdayTopicListResponseOk;
                    topics.push(...(okData.items ?? []));
                    page = okData.nextPage ?? null;
                    continue;
                }

                return {
                    ok: true,
                    statusCode: response.status,
                    message:
                        typeof data === 'string'
                            ? data
                            : 'Workday topics retrieved (unknown format)',
                    topics: null,
                };
            }

            const errorData = response.data as WorkdayTopicListResponseClientError;
            return {
                ok: false,
                statusCode: errorData.status ?? response.status,
                message:
                    errorData.title ??
                    response.statusText ??
                    'Error retrieving Workday topics',
                topics: null,
                error: errorData,
            };
        }

        return {
            ok: true,
            statusCode: lastResponse?.status ?? 200,
            message: 'Workday topics retrieved successfully',
            topics,
        };
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const statusCode = error.response?.status ?? 500;
            const message =
                error.response?.data?.message ||
                error.message ||
                'An error occurred while retrieving Workday topics.';

            return {
                ok: false,
                statusCode,
                message,
                topics: null,
                error: error.response?.data ?? error.message,
            };
        }

        return {
            ok: false,
            statusCode: 500,
            message:
                (error as Error)?.message ??
                'Unknown error while retrieving Workday topics.',
            topics: null,
            error: `${error}`,
        };
    }
};


export const getEmployeeCertifications = async (): Promise<WorkdayEmployeeCertificationListUnifiedResponse> => {
    try {
        const response: AxiosResponse<WorkdayEmployeeCertification[] | string> =
            await http.get('/listEmployeeCertifications');

        if (response.status >= 200 && response.status < 300) {
            const data = response.data;

            if (Array.isArray(data)) {
                return {
                    ok: true,
                    statusCode: response.status,
                    message: 'Employee certifications retrieved successfully',
                    employeeCertifications: data,
                };
            }

            return {
                ok: true,
                statusCode: response.status,
                message:
                    typeof data === 'string'
                        ? data
                        : 'Employee certifications retrieved (unknown format)',
                employeeCertifications: null,
            };
        }

        return {
            ok: false,
            statusCode: response.status,
            message: response.statusText ?? 'Error retrieving employee certifications',
            employeeCertifications: null,
        };
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const statusCode = error.response?.status ?? 500;
            const message =
                error.response?.data?.message ||
                error.message ||
                'An error occurred while retrieving employee certifications.';

            return {
                ok: false,
                statusCode,
                message,
                employeeCertifications: null,
                error: error.response?.data ?? error.message,
            };
        }

        return {
            ok: false,
            statusCode: 500,
            message:
                (error as Error)?.message ??
                'Unknown error while retrieving employee certifications.',
            employeeCertifications: null,
            error: `${error}`,
        };
    }
};


export const linkTopicToCertification = async (
    certificationId: number,
    topicId: number,
    userEmail: string,
) => {
    let response = { data: null, status: 0, statusText: '', headers: {}, config: {} } as AxiosResponse;
    try {
        response = await http.post('/linkTopicToCertification', { certificationId, topicId, userEmail });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) response = error.response;
    }
    return response;
};


export const removeEmployeeCertification = async (
    employeeCertificationId: number,
    userEmail: string,
) => {
    let response = { data: null, status: 0, statusText: '', headers: {}, config: {} } as AxiosResponse;
    try {
        response = await http.post('/removeEmployeeCertification', { employeeCertificationId, userEmail });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) response = error.response;
    }
    return response;
};


export const updateEmployeeCertification = async (
    employeeCertificationId: number,
    issuedDate: string | null,
    expirationDate: string | null,
    userEmail: string,
    rowVersion: string,
    certifiedById?: number | null,
) => {
    let response = { data: null, status: 0, statusText: '', headers: {}, config: {} } as AxiosResponse;
    try {
        response = await http.post('/updateEmployeeCertification', {
            employeeCertificationId,
            issuedDate,
            expirationDate,
            certifiedById: certifiedById ?? null,
            userEmail,
            rowVersion,
        });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) response = error.response;
    }
    return response;
};


export const unlinkTopicFromCertification = async (
    certificationId: number,
    topicId: number,
    userEmail: string,
) => {
    let response = { data: null, status: 0, statusText: '', headers: {}, config: {} } as AxiosResponse;
    try {
        response = await http.post('/unlinkTopicFromCertification', { certificationId, topicId, userEmail });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) response = error.response;
    }
    return response;
};


export const certifyEmployee = async (
    employeeId: number,
    certificationId: number,
    issuedDate: string,
    expirationDate: string,
    certifiedById: number,
    userEmail: string,
) => {
    let response = { data: null, status: 0, statusText: '', headers: {}, config: {} } as AxiosResponse;
    try {
        response = await http.post('/certifyEmployee', {
            employeeId,
            certificationId,
            issuedDate,
            expirationDate,
            certifiedById,
            userEmail,
        });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) response = error.response;
    }
    return response;
};


